import { useState, useEffect } from "react";
import { useNavigate, createFileRoute } from "@tanstack/react-router";
import { Plus, Settings } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Header } from "@/components/shared/header";
import { PageContainer } from "@/components/shared/page-container";
import { useLoadingIndicator } from "@/lib/hooks/use-loading-indicator";
import { useAlertDialogStore } from "@/lib/store/use-alert-dialog-store";

// FSD imports
import { useNotices, useDeleteNotice } from "@/features/notice";
import { NoticeTable } from "@/widgets/notice";

interface CurrentUser {
  _id: string;
  role: string;
}

function NoticePage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const { setAlertDialogState, resetAlertDialogState } = useAlertDialogStore();

  useEffect(() => {
    const stored = localStorage.getItem("growing-teacher-user");
    if (stored) {
      setCurrentUser(JSON.parse(stored));
    }
  }, []);

  const { data: noticesData, isLoading } = useNotices({
    page,
    limit: 20,
  });
  const deleteNotice = useDeleteNotice();

  const handleDelete = (id: string) => {
    setAlertDialogState({
      title: "공지사항 삭제",
      description: "정말 삭제하시겠습니까?",
      confirmText: "삭제",
      onConfirm: async () => {
        resetAlertDialogState();
        try {
          await deleteNotice.mutateAsync(id);
          toast.success("삭제되었습니다.");
        } catch {
          toast.error("삭제에 실패했습니다.");
        }
      },
      onCancel: () => {
        resetAlertDialogState();
      },
    });
  };

  useLoadingIndicator(isLoading);

  const canEditNotice = (authorId: string) => {
    if (!currentUser) return false;
    return currentUser.role === "admin" || currentUser._id === authorId;
  };

  return (
    <PageContainer>
      <Header
        title="공지사항 관리"
        right={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate({ to: "/notice/categories" })}
            >
              <Settings className="size-4 mr-2" />
              카테고리 관리
            </Button>
            <Button onClick={() => navigate({ to: "/notice/new" })}>
              <Plus className="size-4 mr-2" />새 공지사항
            </Button>
          </div>
        }
      />

      <div className="bg-card rounded-lg border">
        <NoticeTable
          notices={noticesData?.notices || []}
          totalPages={noticesData?.totalPages || 1}
          currentPage={page}
          onPageChange={setPage}
          canEdit={canEditNotice}
          onDelete={handleDelete}
        />
      </div>
    </PageContainer>
  );
}

export const Route = createFileRoute("/_content/notice/")({
  component: NoticePage,
});
